import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Key,
  Server,
  Plus,
  Trash2,
  RefreshCw,
  Copy,
  Check,
  LogOut,
  ChevronDown,
  ChevronUp,
  Shield,
  User,
  Upload,
  X,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

const API_BASE = "https://agssoft.ar"

// ─── Types ─────────────────────────────────────────────────────────────────────

interface AfipCuit {
  id: number;
  cuit: string;
  alias: string;
  environment: "HOMO" | "PROD";
  createdAt: string;
}

interface UserCuitAssignment {
  afipCuit: {
    id: number;
    cuit: string;
    alias: string;
    environment: "HOMO" | "PROD";
  };
}

interface AdminUser {
  id: number;
  username: string;
  role: "ADMIN" | "USER";
  hasApiToken: boolean;
  createdAt: string;
  afipCuits: UserCuitAssignment[];
}

interface Toast {
  id: number;
  message: string;
  type: "success" | "error";
}

// ─── API helpers ───────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token") ?? "";
  return { Authorization: `Bearer ${token}` };
}

async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response | null> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers ?? {}) },
  });
  if (res.status === 401) {
    localStorage.removeItem("token");
    window.location.href = "/";
    return null;
  }
  return res;
}

// ─── Root component ────────────────────────────────────────────────────────────

export default function AdminPanel() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"cuits" | "users">("cuits");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/");
  }, [navigate]);

  const toast = useCallback(
    (message: string, type: "success" | "error" = "success") => {
      const id = ++toastId.current;
      setToasts((t) => [...t, { id, message, type }]);
      setTimeout(
        () => setToasts((t) => t.filter((x) => x.id !== id)),
        3500
      );
    },
    []
  );

  const handleLogout = async () => {
    await apiFetch("/api/logout", { method: "POST" });
    localStorage.removeItem("token");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-blue-400" />
          <span className="text-lg font-semibold">Panel de Administración</span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-100 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Cerrar sesión
        </button>
      </header>

      {/* Tabs */}
      <div className="bg-gray-800 border-b border-gray-700 px-6">
        <div className="flex gap-1">
          {(["cuits", "users"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              {t === "cuits" ? (
                <span className="flex items-center gap-2">
                  <Server className="h-4 w-4" /> CUITs
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" /> Usuarios
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto p-6">
        {tab === "cuits" && <CuitsTab toast={toast} />}
        {tab === "users" && <UsersTab toast={toast} />}
      </main>

      {/* Toasts */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              t.type === "success"
                ? "bg-green-700 text-white"
                : "bg-red-700 text-white"
            }`}
          >
            {t.type === "success" ? (
              <CheckCircle className="h-4 w-4 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0" />
            )}
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CUITs Tab ─────────────────────────────────────────────────────────────────

function CuitsTab({
  toast,
}: {
  toast: (msg: string, type?: "success" | "error") => void;
}) {
  const [cuits, setCuits] = useState<AfipCuit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [cuit, setCuit] = useState("");
  const [alias, setAlias] = useState("");
  const [environment, setEnvironment] = useState<"HOMO" | "PROD">("HOMO");
  const [certFile, setCertFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiFetch("/api/admin/cuits");
    if (res?.ok) setCuits(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!certFile || !keyFile) {
      toast("Seleccioná ambos archivos (certificado y clave)", "error");
      return;
    }
    setSubmitting(true);
    const form = new FormData();
    form.append("cuit", cuit.replace(/\D/g, ""));
    form.append("alias", alias);
    form.append("environment", environment);
    form.append("cert", certFile);
    form.append("key", keyFile);
    const res = await apiFetch("/api/admin/cuits", { method: "POST", body: form });
    if (res?.ok) {
      toast("CUIT registrado correctamente");
      setCuit("");
      setAlias("");
      setCertFile(null);
      setKeyFile(null);
      setShowForm(false);
      load();
    } else {
      const data = await res?.json();
      toast(data?.error ?? "Error al registrar CUIT", "error");
    }
    setSubmitting(false);
  };

  const handleDelete = async (id: number, cuitStr: string) => {
    if (
      !window.confirm(
        `¿Eliminar CUIT ${cuitStr}? Esto revocará todos los accesos asociados.`
      )
    )
      return;
    const res = await apiFetch(`/api/admin/cuits/${id}`, { method: "DELETE" });
    if (res?.ok) {
      toast("CUIT eliminado");
      load();
    } else toast("Error al eliminar", "error");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">CUITs Registrados</h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
        >
          {showForm ? (
            <X className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {showForm ? "Cancelar" : "Agregar CUIT"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleAdd}
          className="bg-gray-800 rounded-lg p-5 border border-gray-700 space-y-4"
        >
          <h3 className="font-medium text-gray-200">Nuevo CUIT</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">CUIT</label>
              <input
                required
                value={cuit}
                onChange={(e) => setCuit(e.target.value)}
                placeholder="20-12345678-9"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Alias</label>
              <input
                required
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="Mi empresa S.A."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Entorno
              </label>
              <select
                value={environment}
                onChange={(e) =>
                  setEnvironment(e.target.value as "HOMO" | "PROD")
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="HOMO">Homologación (testing)</option>
                <option value="PROD">Producción</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FileInput
              label="Certificado (.pem)"
              accept=".pem,.crt"
              file={certFile}
              onChange={setCertFile}
            />
            <FileInput
              label="Clave privada (.key)"
              accept=".key,.pem"
              file={keyFile}
              onChange={setKeyFile}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
            >
              {submitting ? "Registrando..." : "Registrar CUIT"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : cuits.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No hay CUITs registrados
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-900 border-b border-gray-700">
                <th className="text-left px-4 py-3 text-gray-400 font-medium">
                  CUIT
                </th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">
                  Alias
                </th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">
                  Entorno
                </th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">
                  Registrado
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {cuits.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-gray-700 last:border-0 hover:bg-gray-750 transition-colors"
                >
                  <td className="px-4 py-3 font-mono">{c.cuit}</td>
                  <td className="px-4 py-3">{c.alias}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.environment === "PROD"
                          ? "bg-green-900 text-green-300"
                          : "bg-yellow-900 text-yellow-300"
                      }`}
                    >
                      {c.environment}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {new Date(c.createdAt).toLocaleDateString("es-AR")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(c.id, c.cuit)}
                      className="p-1.5 text-red-400 hover:bg-red-950 rounded transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Users Tab ─────────────────────────────────────────────────────────────────

function UsersTab({
  toast,
}: {
  toast: (msg: string, type?: "success" | "error") => void;
}) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [allCuits, setAllCuits] = useState<AfipCuit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedUser, setExpandedUser] = useState<number | null>(null);

  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<"USER" | "ADMIN">("USER");
  const [submitting, setSubmitting] = useState(false);

  const [revealedToken, setRevealedToken] = useState<{
    userId: number;
    token: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const [changingPwdFor, setChangingPwdFor] = useState<number | null>(null);
  const [newPwd, setNewPwd] = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [uRes, cRes] = await Promise.all([
      apiFetch("/api/admin/users"),
      apiFetch("/api/admin/cuits"),
    ]);
    if (uRes?.ok) setUsers(await uRes.json());
    if (cRes?.ok) setAllCuits(await cRes.json());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await apiFetch("/api/admin/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: newUsername,
        password: newPassword,
        role: newRole,
      }),
    });
    if (res?.ok) {
      toast("Usuario creado");
      setNewUsername("");
      setNewPassword("");
      setNewRole("USER");
      setShowForm(false);
      load();
    } else {
      const data = await res?.json();
      toast(data?.error ?? "Error al crear usuario", "error");
    }
    setSubmitting(false);
  };

  const handleDelete = async (id: number, username: string) => {
    if (!window.confirm(`¿Eliminar al usuario "${username}"?`)) return;
    const res = await apiFetch(`/api/admin/users/${id}`, { method: "DELETE" });
    if (res?.ok) {
      toast("Usuario eliminado");
      load();
    } else toast("Error al eliminar", "error");
  };

  const handleGenerateToken = async (id: number) => {
    const res = await apiFetch(`/api/admin/users/${id}/api-token`, {
      method: "POST",
    });
    if (res?.ok) {
      const data = await res.json();
      setRevealedToken({ userId: id, token: data.token });
      load();
    } else {
      const data = await res?.json();
      toast(data?.error ?? "Error generando token", "error");
    }
  };

  const handleRevokeToken = async (id: number) => {
    if (!window.confirm("¿Revocar el API token de este usuario?")) return;
    const res = await apiFetch(`/api/admin/users/${id}/api-token`, {
      method: "DELETE",
    });
    if (res?.ok) {
      toast("Token revocado");
      load();
    } else toast("Error al revocar", "error");
  };

  const handleChangePassword = async (id: number) => {
    if (!newPwd || newPwd.length < 8) {
      toast("La contraseña debe tener al menos 8 caracteres", "error");
      return;
    }
    const res = await apiFetch(`/api/admin/users/${id}/password`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password: newPwd }),
    });
    if (res?.ok) {
      toast("Contraseña actualizada");
      setChangingPwdFor(null);
      setNewPwd("");
    } else toast("Error al cambiar contraseña", "error");
  };

  const handleToggleCuit = async (
    userId: number,
    cuitId: number,
    hasAccess: boolean
  ) => {
    const res = await apiFetch(
      `/api/admin/users/${userId}/cuits/${cuitId}`,
      { method: hasAccess ? "DELETE" : "POST" }
    );
    if (res?.ok) load();
    else toast("Error al actualizar permisos", "error");
  };

  const copyToken = async (token: string) => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Usuarios</h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
        >
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? "Cancelar" : "Agregar usuario"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-gray-800 rounded-lg p-5 border border-gray-700 space-y-4"
        >
          <h3 className="font-medium text-gray-200">Nuevo usuario</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Usuario
              </label>
              <input
                required
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="nombre_usuario"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Contraseña
              </label>
              <input
                required
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Rol</label>
              <select
                value={newRole}
                onChange={(e) =>
                  setNewRole(e.target.value as "USER" | "ADMIN")
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="USER">Usuario (API token)</option>
                <option value="ADMIN">Administrador (JWT)</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
            >
              {submitting ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No hay usuarios</div>
      ) : (
        <div className="space-y-2">
          {users.map((user) => {
            const isExpanded = expandedUser === user.id;
            const userCuitIds = new Set(
              user.afipCuits.map((a) => a.afipCuit.id)
            );

            return (
              <div
                key={user.id}
                className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden"
              >
                {/* Row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() =>
                      setExpandedUser(isExpanded ? null : user.id)
                    }
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{user.username}</span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.role === "ADMIN"
                              ? "bg-purple-900 text-purple-300"
                              : "bg-gray-700 text-gray-300"
                          }`}
                        >
                          {user.role}
                        </span>
                        {user.role === "USER" && (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.hasApiToken
                                ? "bg-green-900 text-green-300"
                                : "bg-gray-700 text-gray-500"
                            }`}
                          >
                            {user.hasApiToken ? "Token activo" : "Sin token"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {user.afipCuits.length} CUIT
                        {user.afipCuits.length !== 1 ? "s" : ""} asignado
                        {user.afipCuits.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(user.id, user.username)}
                    className="p-1.5 text-red-400 hover:bg-red-950 rounded transition-colors shrink-0"
                    title="Eliminar usuario"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-gray-700 px-4 py-5 space-y-6">
                    {/* API Token — USER only */}
                    {user.role === "USER" && (
                      <section>
                        <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                          <Key className="h-4 w-4 text-yellow-400" />
                          API Token
                        </h4>
                        <div className="flex flex-wrap items-center gap-2">
                          {user.hasApiToken ? (
                            <>
                              <span className="text-sm text-green-400">
                                Token activo
                              </span>
                              <button
                                onClick={() => handleGenerateToken(user.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-yellow-700 hover:bg-yellow-600 rounded text-white transition-colors"
                              >
                                <RefreshCw className="h-3 w-3" /> Regenerar
                              </button>
                              <button
                                onClick={() => handleRevokeToken(user.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-800 hover:bg-red-700 rounded text-white transition-colors"
                              >
                                <X className="h-3 w-3" /> Revocar
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleGenerateToken(user.id)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-700 hover:bg-blue-600 rounded text-white transition-colors"
                            >
                              <Plus className="h-3 w-3" /> Generar token
                            </button>
                          )}
                        </div>
                      </section>
                    )}

                    {/* Change password */}
                    <section>
                      <h4 className="text-sm font-medium text-gray-300 mb-3">
                        Contraseña
                      </h4>
                      {changingPwdFor === user.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="relative">
                            <input
                              type={showNewPwd ? "text" : "password"}
                              value={newPwd}
                              onChange={(e) => setNewPwd(e.target.value)}
                              placeholder="Nueva contraseña (mín. 8)"
                              className="px-3 py-1.5 pr-9 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPwd((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                            >
                              {showNewPwd ? (
                                <AlertCircle className="h-4 w-4" />
                              ) : (
                                <AlertCircle className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <button
                            onClick={() => handleChangePassword(user.id)}
                            className="px-3 py-1.5 text-xs bg-green-700 hover:bg-green-600 rounded text-white transition-colors"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setChangingPwdFor(null);
                              setNewPwd("");
                            }}
                            className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 rounded text-gray-300 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setChangingPwdFor(user.id)}
                          className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 rounded text-gray-300 transition-colors"
                        >
                          Cambiar contraseña
                        </button>
                      )}
                    </section>

                    {/* CUIT permissions */}
                    <section>
                      <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                        <Server className="h-4 w-4 text-blue-400" />
                        Acceso a CUITs
                      </h4>
                      {allCuits.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          No hay CUITs registrados aún
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {allCuits.map((c) => {
                            const has = userCuitIds.has(c.id);
                            return (
                              <label
                                key={c.id}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${
                                  has
                                    ? "border-blue-600 bg-blue-950 text-blue-200"
                                    : "border-gray-700 bg-gray-900 text-gray-400 hover:border-gray-600"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={has}
                                  onChange={() =>
                                    handleToggleCuit(user.id, c.id, has)
                                  }
                                  className="rounded accent-blue-500"
                                />
                                <div>
                                  <div className="text-sm font-mono font-medium">
                                    {c.cuit}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {c.alias} ·{" "}
                                    <span
                                      className={
                                        c.environment === "PROD"
                                          ? "text-green-400"
                                          : "text-yellow-400"
                                      }
                                    >
                                      {c.environment}
                                    </span>
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Token reveal modal */}
      {revealedToken && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-yellow-600 p-6 max-w-lg w-full space-y-4 shadow-2xl">
            <div className="flex items-center gap-2 text-yellow-400">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <h3 className="font-semibold">
                API Token generado — guardalo ahora
              </h3>
            </div>
            <p className="text-sm text-gray-400">
              Este token{" "}
              <strong className="text-gray-200">no puede volver a mostrarse</strong>.
              Copialo y entregáselo al usuario de forma segura.
            </p>
            <div className="flex items-start gap-2">
              <code className="flex-1 bg-gray-900 rounded-lg px-3 py-2 text-green-400 text-xs font-mono break-all leading-relaxed">
                {revealedToken.token}
              </code>
              <button
                onClick={() => copyToken(revealedToken.token)}
                className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors shrink-0 mt-0.5"
                title="Copiar"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-300" />
                )}
              </button>
            </div>
            <button
              onClick={() => {
                setRevealedToken(null);
                setCopied(false);
              }}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
            >
              Lo guardé, cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── File input ────────────────────────────────────────────────────────────────

function FileInput({
  label,
  accept,
  file,
  onChange,
}: {
  label: string;
  accept: string;
  file: File | null;
  onChange: (f: File | null) => void;
}) {
  return (
    <div>
      <label className="block text-sm text-gray-400 mb-1">{label}</label>
      <label
        className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
          file
            ? "border-green-600 bg-green-950 text-green-300"
            : "border-gray-600 bg-gray-700 text-gray-400 hover:border-gray-500"
        }`}
      >
        <Upload className="h-4 w-4 shrink-0" />
        <span className="text-sm truncate">
          {file ? file.name : "Seleccionar archivo..."}
        </span>
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        />
      </label>
    </div>
  );
}
